import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_payment-links-hub-1/artifacts/qmtwufa4_test%20logo%20.jpg";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem("admin_token");
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { if (res.data.role === "admin") navigate("/admin"); })
        .catch(() => localStorage.removeItem("admin_token"));
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.role !== "admin") {
        setError("Acces reserve aux administrateurs");
        setLoading(false);
        return;
      }
      localStorage.setItem("admin_token", res.data.token);
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else if (!err.response) {
        // Network / CORS error — no response received
        setError("Impossible de contacter le serveur. Verifiez votre connexion internet.");
      } else {
        setError("Erreur de connexion (code " + err.response.status + ")");
      }
    }
    setLoading(false);
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="AyaPay" loading="lazy" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0A1128]">Administration</h1>
          <p className="text-sm text-[#475569] mt-1">Connectez-vous pour gerer le site</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg p-3 border border-red-100">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Email</label>
              <Input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@ayapay.ch"
                className="rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Mot de passe</label>
              <div className="relative">
                <Input
                  data-testid="admin-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  className="rounded-lg pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full bg-[#1B2B5B] hover:bg-[#0f1d3d] text-white rounded-lg h-11 font-semibold"
            >
              {loading ? "Connexion..." : "Se connecter"}
              <Lock size={16} className="ml-2" />
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          <a href="/" className="hover:text-[#1B2B5B] transition-colors">Retour au site</a>
        </p>
      </div>
    </div>
  );
}
